import { callApi } from "@/api/callApi";
import { API_ENDPOINT, API_URL_USER_SERVICE } from "@/api/constant";
import CommonTable, { TableColumn } from "@/components/common/CommonTable";
import ConfirmationModel from '@/components/common/models/ConfrimationModel';
import PageHeading from "@/components/common/PageHeading";
import Layout from "@/components/Layout/Layout";
import Card from "@/components/UI/Card";
import { usePermissions } from "@/hooks/usePermissions";
import { ActionConfig } from "@/interfaces/common.interface";
import { IRolesResponse } from "@/interfaces/RolesResponse.interface";
import { formatDateTime, showToastNotification } from "@/utility/helper";
import { RoutesLink } from "@/utility/RouteLinks";
import { Plus } from "lucide-react";
import { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";

interface StatesProp {
    isLoading: boolean
    showDeleteModel: boolean
    roles: IRolesResponse[]
    roleToDelete: string | null
}
const Roles = () => {
    const navigate = useNavigate();
    const { canCreate, canEdit, canView, canDelete } = usePermissions();
    const [states, setStates] = useState<StatesProp>({
        isLoading: false,
        roles: [],
        showDeleteModel: false,
        roleToDelete: null
    });
    const handleChangeState = (newState: Partial<typeof states>) => {
        setStates((prev) => ({
            ...prev,
            ...newState,
        }));
    };

    const fetchRoles = async () => {
        try {
            handleChangeState({ isLoading: true })
            const PATH = `${API_URL_USER_SERVICE}${API_ENDPOINT.roles}`
            const res = await callApi<IRolesResponse[]>(PATH, { method: 'GET' });
            handleChangeState({ roles: res })
        } catch (error) {
            const err = error as { message: string };
            showToastNotification('danger', err.message);
            console.log('error in role submit', error);
        } finally {
            handleChangeState({ isLoading: false })
        }
    }

    const deleteRole = async (id: string) => {
        try {
            const PATH = `${API_URL_USER_SERVICE}/${API_ENDPOINT.roles}/${id}`;
            await callApi(PATH, { method: 'DELETE' })
            fetchRoles()
        } catch (error) {
            const err = error as { message: string }
            showToastNotification('danger', err.message);
            console.log('error in delete role', err)
        }
    }

    useEffect(() => {
        fetchRoles()
    }, [])

    const columns: TableColumn<IRolesResponse>[] = [
        {
            key: 'name',
            header: "Name"
        },
        {
            key: 'createdAt',
            header: 'Created At',
            render: (item) => {
                return (
                    <span>{formatDateTime(item.createdAt, 'YYMMMDDHH')}</span>
                )
            }
        },
        {
            key: 'updatedAt',
            header: 'Last Update',
            render: (item) => {
                return (
                    <span>{formatDateTime(item.updatedAt, 'YYMMMDDHH')}</span>
                )
            }
        },
    ];

    const roleActionConfig: Record<string, ActionConfig<IRolesResponse>> = {
        view: {
            text: 'View',
            onClick: item => navigate(`${RoutesLink.viewRole}/${item.id}`),
            permission: () => canView('role'),
        },
        eidt: {
            text: 'Edit',
            onClick: (item) => navigate(`${RoutesLink.addRoles}/${item.id}`),
            permission: () => canEdit('role'),
        },
        delete: {
            text: 'delete',
            onClick: (item) => handleChangeState({ showDeleteModel: true, roleToDelete: item.id }),
            permission: () => canDelete('role'),
        }
    }

    const actions = useMemo(
        () =>
            Object.values(roleActionConfig)
                .filter(action => action.permission())
                .map(({ permission, ...rest }) => rest),
        [canView, canEdit, canDelete]
    );
    return (
        <Layout>
            <PageHeading
                text="Roles"
                button={
                    canCreate('role') ?
                        {
                            text: "Add Role",
                            icon: <Plus size={18} />,
                            onClick: () => navigate(RoutesLink.addRoles)
                        } : undefined}
            />
            <Card>
                <CommonTable
                    columns={columns}
                    data={states.roles}
                    loading={states.isLoading}
                    actions={actions}
                />
            </Card>
            {
                states.showDeleteModel && (
                    <ConfirmationModel
                        show={true}
                        onClose={() => { handleChangeState({ showDeleteModel: false, roleToDelete: null }) }}
                        type='role'
                        onConfirm={() => deleteRole(states.roleToDelete as string)}
                    />
                )
            }
        </Layout>
    )
}

export default Roles;